import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '../shadComponents/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../shadComponents/ui/dialog';

interface Props {
    onUpload: (file: File) => Promise<void>;
    isUploading: boolean;
}

const ReferencePhotoUploader: React.FC<Props> = ({ onUpload, isUploading }) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5,
    });
    const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);
    const [showCropDialog, setShowCropDialog] = useState(false);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (file) {
            setSelectedFile(file);
            setShowCropDialog(true);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'image/*': ['.jpeg', '.jpg', '.png'],
        },
        maxFiles: 1,
        disabled: isUploading,
    });

    const handleCropComplete = async () => {
        if (!selectedFile || !imageRef) return;

        const canvas = document.createElement('canvas');
        const scaleX = imageRef.naturalWidth / imageRef.width;
        const scaleY = imageRef.naturalHeight / imageRef.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext('2d');

        if (!ctx) return;

        ctx.drawImage(
            imageRef,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height,
        );

        // Convert canvas to blob
        canvas.toBlob(async (blob) => {
            if (!blob) return;

            // Create a new file from the blob
            const croppedFile = new File([blob], selectedFile.name, {
                type: selectedFile.type,
                lastModified: Date.now(),
            });

            await onUpload(croppedFile);
            setShowCropDialog(false);
            setSelectedFile(null);
        }, selectedFile.type);
    };

    return (
        <>
            <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
                    ${
                        isDragActive
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300 bg-gray-50'
                    }
                    ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                <input {...getInputProps()} />
                <div className="text-gray-500">
                    {isUploading ? (
                        'Uploading...'
                    ) : isDragActive ? (
                        'Drop the photo here'
                    ) : (
                        <>
                            <p>
                                Drag and drop a photo here, or click to select
                            </p>
                            <p className="text-sm text-gray-400 mt-2">
                                Supported formats: JPEG, PNG
                            </p>
                        </>
                    )}
                </div>
            </div>

            <Dialog open={showCropDialog} onOpenChange={setShowCropDialog}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>Crop Photo</DialogTitle>
                    </DialogHeader>
                    {selectedFile && (
                        <div className="mt-4">
                            <ReactCrop
                                crop={crop}
                                onChange={(c) => setCrop(c)}
                                aspect={1}
                                className="max-h-[60vh]"
                            >
                                <img
                                    ref={setImageRef}
                                    src={URL.createObjectURL(selectedFile)}
                                    alt="Crop preview"
                                    className="max-w-full"
                                />
                            </ReactCrop>
                        </div>
                    )}
                    <DialogFooter className="mt-4">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowCropDialog(false);
                                setSelectedFile(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleCropComplete}>
                            Crop & Upload
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ReferencePhotoUploader;
